<template>
  <div class="payment-page">
    <div class="payment-container">

      <!-- Loading State -->
      <div v-if="pageState === 'loading'" class="state-container">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-3 text-muted">Loading payment information...</p>
      </div>

      <!-- Error State (API failure on load) -->
      <div v-else-if="pageState === 'error'" class="state-container">
        <div class="error-icon">!</div>
        <h2 class="mt-3">Something went wrong</h2>
        <p class="text-muted">{{ errorMessage }}</p>
        <button class="btn btn-outline-primary mt-3" @click="init">Try Again</button>
      </div>

      <!-- Expired State -->
      <div v-else-if="pageState === 'expired'" class="state-container">
        <div class="expired-icon">&#x23F0;</div>
        <h2 class="mt-3">Payment Link Expired</h2>
        <p class="text-muted">This payment link has expired. Please contact the practice for a new link.</p>
      </div>

      <!-- Already Paid State -->
      <div v-else-if="pageState === 'paid'" class="state-container">
        <div v-if="paymentData && paymentData.logoUrl" class="logo-wrapper">
          <img class="practice-logo" :src="paymentData.logoUrl" :alt="paymentData.practiceName" />
        </div>
        <div class="success-checkmark">&#x2713;</div>
        <h2 class="mt-3">Payment Received</h2>
        <p class="paid-amount">${{ formatAmount(paymentData.amount) }}</p>
        <p class="text-muted" v-if="paymentData.paidDate">Paid on {{ formatDate(paymentData.paidDate) }}</p>
        <p class="text-muted" v-if="paymentData.patientName">{{ paymentData.patientName }}</p>
      </div>

      <!-- Success State (just paid) -->
      <div v-else-if="pageState === 'success'" class="state-container">
        <div v-if="paymentData && paymentData.logoUrl" class="logo-wrapper">
          <img class="practice-logo" :src="paymentData.logoUrl" :alt="paymentData.practiceName" />
        </div>
        <div class="success-checkmark">&#x2713;</div>
        <h2 class="mt-3">Payment Successful!</h2>
        <p class="paid-amount">${{ formatAmount(paymentData.amount) }}</p>
        <p class="text-muted">Thank you for your payment, {{ paymentData.patientName }}.</p>
      </div>

      <!-- Payment Form State (pending/sent/failed) -->
      <div v-else-if="pageState === 'form' || pageState === 'failed'" class="state-container">
        <div v-if="paymentData && paymentData.logoUrl" class="logo-wrapper">
          <img class="practice-logo" :src="paymentData.logoUrl" :alt="paymentData.practiceName" />
        </div>
        <h2 v-if="paymentData && paymentData.practiceName" class="practice-name">{{ paymentData.practiceName }}</h2>

        <p class="amount-due-label">Amount Due</p>
        <p class="amount-due">${{ formatAmount(paymentData.amount) }}</p>

        <p class="patient-name" v-if="paymentData && paymentData.patientName">{{ paymentData.patientName }}</p>

        <!-- Error message for failed payments -->
        <div v-if="errorMessage" class="error-banner">
          {{ errorMessage }}
        </div>

        <div id="card-element" class="card-element-wrapper">
          <!-- Moolah collect.js iFrame will be inserted here -->
        </div>

        <button
          class="pay-button"
          :disabled="!collectReady || processing"
          @click="makePayment"
        >
          <span v-if="!processing">Pay ${{ formatAmount(paymentData.amount) }}</span>
          <span v-else>
            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
            Processing payment...
          </span>
        </button>
      </div>

      <!-- Processing Overlay -->
      <div v-if="processing" class="processing-overlay">
        <div class="spinner-border text-light" role="status">
          <span class="sr-only">Processing...</span>
        </div>
        <p class="mt-3 text-light">Processing your payment...</p>
      </div>

      <PoweredByFooter />

    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import PoweredByFooter from '@/components/PoweredByFooter.vue';

export default {
  name: 'ProcessPayment',
  components: { PoweredByFooter },
  props: ['paymentID'],

  data() {
    return {
      pageState: 'loading', // loading | form | paid | expired | success | failed | error
      paymentData: null,
      collect: null,
      collectReady: false,
      processing: false,
      errorMessage: '',
    };
  },

  created() {
    this.init();
  },

  beforeDestroy() {
    this.cleanupCollectScript();
  },

  methods: {
    ...mapActions('moolah', ['getMoolahPaymentInfo', 'processMoolahPayment']),

    async init() {
      this.pageState = 'loading';
      this.errorMessage = '';

      if (!this.paymentID) {
        this.pageState = 'error';
        this.errorMessage = 'Payment ID is missing. Please check the link and try again.';
        return;
      }

      try {
        const raw = await this.getMoolahPaymentInfo(this.paymentID);
        // Map backend field names to frontend-friendly names
        const data = {
          paymentID: raw.paymentID,
          siteID: raw.siteID,
          practiceName: raw.siteName,
          logoUrl: raw.logoUrl,
          patientName: raw.patientName,
          amount: raw.totalCharged || raw.balance,
          status: raw.status,
          paidDate: raw.paidAt,
          expiresAt: raw.expiresAt,
          moolahBusinessID: raw.moolahBusinessID,
          moolahApplicationID: raw.moolahApplicationID,
        };
        this.paymentData = data;

        const status = (data.status || '').toLowerCase();

        if (status === 'paid' || status === 'completed') {
          this.pageState = 'paid';
        } else if (status === 'expired') {
          this.pageState = 'expired';
        } else if (status === 'pending' || status === 'sent') {
          this.pageState = 'form';
          this.$nextTick(() => {
            this.loadCollectJs();
          });
        } else {
          // Unknown status, default to showing the form
          this.pageState = 'form';
          this.$nextTick(() => {
            this.loadCollectJs();
          });
        }
      } catch (err) {
        console.error('Error loading payment info:', err);
        this.pageState = 'error';
        this.errorMessage = 'Unable to load payment information. Please try again.';
      }
    },

    loadCollectJs() {
      // Don't reload if already loaded
      if (this.collect) {
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.poynt.net/collect.js';
      script.onload = () => {
        this.collect = new window.TokenizeJs(this.paymentData.moolahBusinessID, this.paymentData.moolahApplicationID);
        const options = {
          iFrame: {
            width: '100%',
            height: '400px',
            border: '0',
            borderRadius: '8px',
          },
          style: {
            theme: 'default',
          },
          displayComponents: {
            firstName: false,
            lastName: false,
            emailAddress: false,
            submitButton: false,
            showEndingPage: false,
            labels: true,
          },
        };
        this.collect.mount('card-element', document, options);

        this.collect.on('nonce', (response) => {
          this.handleNonce(response.data.nonce);
        });

        this.collect.on('error', (err) => {
          console.error('Collect.js error:', err);
          this.processing = false;
          this.errorMessage = 'There was an error with the card form. Please try again.';
        });

        this.collectReady = true;
      };

      script.onerror = () => {
        console.error('Failed to load collect.js');
        this.errorMessage = 'Failed to load the payment form. Please refresh the page.';
      };

      document.head.appendChild(script);
    },

    makePayment() {
      if (!this.collect || !this.collectReady || this.processing) {
        return;
      }
      this.errorMessage = '';
      this.processing = true;
      this.collect.getNonce({});
    },

    async handleNonce(nonce) {
      try {
        const payload = {
          paymentID: this.paymentID,
          nonce: nonce,
        };
        const result = await this.processMoolahPayment(payload);
        this.processing = false;

        if (result && result.success) {
          this.pageState = 'success';
          // Dismantle Moolah's Collect.js — its iframe is unmounted by the
          // v-else-if when pageState flips, but the underlying TokenizeJs
          // instance + injected script tag stick around and keep network
          // keepalives going. That keeps the browser tab's loading
          // indicator spinning forever after the patient already saw the
          // green "Payment Successful" page (reported 2026-05-08).
          this.cleanupCollectScript();
        } else {
          // Declined or unexpected status (returned as 200 with success: false)
          this.pageState = 'failed';
          this.errorMessage = result?.message || 'Payment was declined. Please try a different card.';
        }
      } catch (err) {
        console.error('Payment processing error:', err);
        this.processing = false;
        this.pageState = 'failed';
        this.errorMessage = err.response?.data?.message || 'Payment failed. Please check your card details and try again.';
      }
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00';
      return parseFloat(amount).toFixed(2);
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    cleanupCollectScript() {
      // Remove the collect.js script tag on destroy
      const scripts = document.head.querySelectorAll('script[src="https://cdn.poynt.net/collect.js"]');
      scripts.forEach((s) => s.remove());
      this.collect = null;
      this.collectReady = false;
    },
  },
};
</script>

<style scoped>
.payment-page {
  min-height: 100vh;
  background-color: #f5f7fa;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.payment-container {
  width: 100%;
  max-width: 500px;
  position: relative;
}

.state-container {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  padding: 40px 32px;
  text-align: center;
}

/* Logo */
.logo-wrapper {
  margin-bottom: 16px;
}

.practice-logo {
  max-height: 80px;
  width: auto;
  max-width: 100%;
}

.practice-name {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 24px;
}

/* Amount */
.amount-due-label {
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.amount-due {
  font-size: 2rem;
  font-weight: 700;
  color: #2e7d32;
  margin-bottom: 8px;
}

.patient-name {
  font-size: 1rem;
  color: #555;
  margin-bottom: 24px;
}

/* Card element */
.card-element-wrapper {
  margin: 16px 0;
  min-height: 100px;
}

/* Pay button */
.pay-button {
  display: block;
  width: 100%;
  padding: 14px 24px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  background-color: #2e7d32;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 8px;
}

.pay-button:hover:not(:disabled) {
  background-color: #256b29;
}

.pay-button:disabled {
  background-color: #a5d6a7;
  cursor: not-allowed;
}

/* Success checkmark */
.success-checkmark {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #2e7d32;
  color: #ffffff;
  font-size: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px auto 0;
}

.paid-amount {
  font-size: 2rem;
  font-weight: 700;
  color: #2e7d32;
  margin-bottom: 4px;
}

/* Error */
.error-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #d32f2f;
  color: #ffffff;
  font-size: 2.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.error-banner {
  background-color: #fdecea;
  color: #b71c1c;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.9rem;
  text-align: left;
}

/* Expired */
.expired-icon {
  font-size: 3rem;
  margin: 0 auto;
}

/* Processing overlay */
.processing-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* Footer */
.page-footer {
  text-align: center;
  padding: 24px 0 8px;
}

.page-footer a {
  color: #999;
  font-size: 0.8rem;
  text-decoration: none;
}

.page-footer a:hover {
  color: #666;
  text-decoration: underline;
}

/* Mobile responsive */
@media (max-width: 480px) {
  .payment-page {
    padding: 16px 12px;
  }

  .state-container {
    padding: 28px 20px;
  }

  .amount-due {
    font-size: 1.75rem;
  }

  .pay-button {
    font-size: 1rem;
    padding: 12px 20px;
  }
}
</style>
